import { useState, useRef, useEffect, useMemo } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { transition, variants } from '../../variants';
import { useAuth } from '../../../../../../utils/Auth';
import { useFileUpload } from '../../../../../../utils/hooks/useFileUpload';
import styles from './ImageCropper.module.css';

const CROP_WIDTH = 540;
const CROP_HEIGHT = 160;
const STAGE_PADDING = 40;
const SAVE_MULTIPLIER = 2;

export default function ImageCropper(props) {

    const { containerWidth, onHeaderChange, header } = props;
    const { qr_data, dataDispatch } = useAuth();

    const [ imgData, setImgData ] = useState({ src: '', width: 0, height: 0, original: null, name: '' });
    const [ position, setPosition ] = useState({ x: STAGE_PADDING, y: STAGE_PADDING });
    const [ scale, setScale ] = useState(1);
    const [ isDragging, setIsDragging ] = useState(false);
    const [ loading, setLoading ] = useState(false);
    const [ isDragOver, setIsDragOver ] = useState(false);

    const allowedTypes = ['.jpg','.jpeg','.png','.webp','image/jpg','image/jpeg','image/png','image/webp'];

    const { handleFileSelected } = useFileUpload({
        acceptableFileTypes: ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'],
        fileHandleType: 'qr_connect',
    });

    const dragStart = useRef({ x: 0, y: 0 });
  
    const processImageFile = (file) => {
        if (!file) return;

        if (!allowedTypes.includes(file.type)) {
            return;
        }

        const reader = new FileReader();
        reader.onload = (event) => {
            const img = new Image();
            img.onload = () => {
                const baseScale = Math.max(CROP_WIDTH / img.width, CROP_HEIGHT / img.height);

                setImgData({
                    src: event.target.result,
                    width: img.width * baseScale,
                    height: img.height * baseScale,
                    original: img,
                    name: file.name
                });
                setScale(1);
                setPosition({
                    x: STAGE_PADDING + (CROP_WIDTH - (img.width * baseScale)) / 2,
                    y: STAGE_PADDING + (CROP_HEIGHT - (img.height * baseScale)) / 2
                });
            };
            img.src = event.target.result;
        };
        reader.readAsDataURL(file);
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        processImageFile(file);
        // reset input value so new files can be chosen
        e.target.value = '';
    };

    const handleDragOver = (e) => {
        if (imgData.src) return; // Only allow drag if no image exists
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);
    };

    const handleDrop = (e) => {
        if (imgData.src) return; // Only allow drop if no image exists
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);

        const file = e.dataTransfer.files[0];
        processImageFile(file);
    };

    const onMouseDown = (e) => {
        if (!imgData.src) return;
        setIsDragging(true);
        dragStart.current = { x: e.clientX - position.x, y: e.clientY - position.y };
    };

    useEffect(() => {
        const onMouseMove = (e) => {
            if (!isDragging) return;
            setPosition({ x: e.clientX - dragStart.current.x, y: e.clientY - dragStart.current.y });
        };

        const onMouseUp = () => setIsDragging(false);

        if (isDragging) {
            window.addEventListener('mousemove', onMouseMove);
            window.addEventListener('mouseup', onMouseUp);
        }
        return () => {
            window.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('mouseup', onMouseUp);
        };
    }, [isDragging]);

    const saveCroppedImage = async () => {
        if (!imgData.src) return;

        setLoading(true);
        if (!header.hide_text) {
            onHeaderChange({ ...header, hide_text: true, use_image: true });
        }
        try {
            const canvas = document.createElement('canvas');
            canvas.width = CROP_WIDTH * SAVE_MULTIPLIER;
            canvas.height = CROP_HEIGHT * SAVE_MULTIPLIER;
            const ctx = canvas.getContext('2d');
    
            // current size of image on screen (including scale)
            const currentDisplayWidth = imgData.width * scale;
            const currentDisplayHeight = imgData.height * scale;
    
            // ratio between original file and screen display
            const scaleRatio = imgData.original.width / currentDisplayWidth;
    
            // source coordinates (top left corner of crop box) relative to image's top left corner
            const sx = (STAGE_PADDING - position.x) * scaleRatio;
            const sy = (STAGE_PADDING - position.y) * scaleRatio;
    
            // source width/height
            const sWidth = CROP_WIDTH * scaleRatio;
            const sHeight = CROP_HEIGHT * scaleRatio;
    
            ctx.drawImage(
                imgData.original,
                sx,
                sy,
                sWidth,
                sHeight,
                0,
                0,
                CROP_WIDTH * SAVE_MULTIPLIER, 
                CROP_HEIGHT * SAVE_MULTIPLIER
            );
    
            // Convert canvas to blob, then to File object
            await new Promise((resolve) => {
                canvas.toBlob(async (blob) => {
                    if (!blob) {
                        resolve();
                        return;
                    }
    
                    const file = new File([blob], imgData.name || 'header.png', { type: 'image/png' });
                    await handleFileSelected([file]);
                    resolve();
                }, 'image/png', 1.0);
            });
        } catch (err) {
            console.error('Error saving image:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleCropperScale = useMemo(() => {
        if (containerWidth > 650) {
            return 'scale(1)'
        } else if (containerWidth > 475) {
            return 'scale(.75)'
        } else if (containerWidth > 400) {
            return 'scale(.62)'
        } else if (containerWidth > 300) {
            return 'scale(.52)'
        } else return 'scale(.4)'
    },[containerWidth]);

    const handleMargins = useMemo(() => {
        if (containerWidth > 650) {
            return '0'
        } else if (containerWidth > 475) {
            return '-3rem'
        } else if (containerWidth > 400) {
            return '-5rem'
        } else if (containerWidth > 300) {
            return '-6rem'
        }
    },[containerWidth])

    return (
        <div className={styles.container}>
            <h5>Header Image</h5>
            <div className={styles.imageContainer}>
                <div className={styles.uploadContainer}>
                    <label className={styles.imageBtn}>
                        <i className='far fa-upload' />
                        Upload
                        <input type="file" accept={allowedTypes.join(',')} onChange={handleFileChange} />
                    </label>
                    <AnimatePresence initial={false}>
                        {imgData.name ? (
                            <motion.div 
                                className={styles.imgTitle}
                                transition={transition}
                                variants={variants}
                                initial='initial'
                                animate='animate'
                                exit='initial'
                                title={imgData.name}
                            >
                                {imgData.name}
                            </motion.div>
                        ) : (
                            <motion.div 
                                className={styles.imgTitleOptimal}
                                transition={transition}
                                variants={variants}
                                initial='initial'
                                animate='animate'
                                exit='initial'
                            >
                                Optimal Size: 1080w x 320h
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </div>
            <div 
                className={styles.cropWrapper}
                onMouseDown={onMouseDown}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                style={{
                    width: CROP_WIDTH + (STAGE_PADDING * 2),
                    height: CROP_HEIGHT + (STAGE_PADDING * 2),
                    cursor: isDragging ? 'grabbing' : imgData.src ? 'grab' : 'default',
                    transform: handleCropperScale,
                    border: isDragOver ? '2px dashed #607ca7' : undefined,
                    backgroundColor: isDragOver ? 'rgba(96, 124, 167, 0.05)' : undefined
                }}
            >
                {!imgData.src && !isDragOver && (
                    <div className={styles.dropZoneText}>
                        <i className='far fa-cloud-arrow-up' style={{ fontSize: '3rem', marginBottom: '0.5rem', color: '#9ca3af' }} />
                        <p>Drag and drop an image here</p>
                    </div>
                )}
                {isDragOver && (
                    <div className={styles.dropZoneText}>
                        <i className='far fa-cloud-arrow-up' style={{ fontSize: '3rem', marginBottom: '0.5rem', color: '#607ca7' }} />
                        <p style={{ color: '#607ca7', fontWeight: 600 }}>Drop image to upload</p>
                    </div>
                )}
                {imgData.src && (
                    <img
                        src={imgData.src}
                        alt='to crop'
                        draggable={false}
                        style={{
                            left: position.x,
                            top: position.y,
                            width: imgData.width * scale,
                            height: imgData.height * scale
                        }}
                    />
                )}
                <div 
                    className={styles.cropOverlay}
                    style={{
                        top: STAGE_PADDING,
                        left: STAGE_PADDING,
                        width: CROP_WIDTH,
                        height: CROP_HEIGHT,
                    }}
                />
            </div>
            <div className={styles.scaleContainer} style={{marginTop: handleMargins}}>
                <label htmlFor='zoom'>Zoom:</label>
                <input id='zoom' type='range' min='1' max='3' step='0.1' value={scale} onChange={(e) => setScale(parseFloat(e.target.value))} disabled={!imgData.src} style={{accentColor: '#607ca7'}} />
            </div>
            <button className={styles.cropBtn} onClick={saveCroppedImage} disabled={!imgData.src}>
                {!loading ? 'Save Header Image' : <i className='far fa-spinner fa-spin-pulse' />}
            </button>
        </div>
    );
};